import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Button,
  Flex,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  message,
} from 'antd'
import type { TableProps } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import Title from 'antd/es/typography/Title'
import type { ManualSpendEntry } from '@/data'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { getMarketingSpend } from '@/api/reporting/marketing-spend/getMarketingSpend'
import { createMarketingSpend } from '@/api/reporting/marketing-spend/createMarketingSpend'
import { updateMarketingSpend } from '@/api/reporting/marketing-spend/updateMarketingSpend'
import { deleteMarketingSpend } from '@/api/reporting/marketing-spend/deleteMarketingSpend'
import { resolveBackendSort } from '@/utils/backendSort'
import { getErrorMessage } from '@/utils/getErrorMessage'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function toDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  return `${Number(m)}/${Number(d)}/${y}`
}

export const ManualSpendPage: React.FC = () => {
  const now = new Date()
  const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const auth = useContext(AuthContext)!

  const canViewCreativeSpend = hasPermission(
    auth.user,
    'lop:ui:field:read:marketing-spend:creative_spend',
  )
  const canCreate = hasPermission(
    auth.user,
    'lop:ui:action:create:reporting:manual-spend',
  )
  const canUpdate = hasPermission(
    auth.user,
    'lop:ui:action:update:reporting:manual-spend',
  )
  const canDelete = hasPermission(
    auth.user,
    'lop:ui:action:delete:reporting:manual-spend',
  )

  const userPartners = useMemo(() => auth.user?.partners ?? [], [auth.user])
  const [selectedPartner, setSelectedPartner] = useState<number | null>(null)
  const [startDate, setStartDate] = useState(toDateString(mtdStart))
  const [endDate, setEndDate] = useState(toDateString(now))
  const [spendData, setSpendData] = useState<ManualSpendEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [sortField, setSortField] = useState('-spend_date')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<ManualSpendEntry | null>(
    null,
  )
  const [newDate, setNewDate] = useState('')
  const [newAdSpend, setNewAdSpend] = useState<number | null>(null)
  const [newCreativeSpend, setNewCreativeSpend] = useState<number | null>(null)

  const columnSortKeys = {
    spend_date: 'spend_date',
    ad_spend: 'ad_spend',
    creative_spend: 'creative_spend',
  } as const

  useEffect(() => {
    if (selectedPartner == null && userPartners.length > 0) {
      setSelectedPartner(userPartners[0].affiliate_id)
    }
  }, [userPartners, selectedPartner])

  const partnerOptions = useMemo(
    () => userPartners.map((p) => ({ value: p.affiliate_id, label: p.name })),
    [userPartners],
  )

  const fetchSpend = useCallback(
    async (page = 1, sort = sortField) => {
      if (selectedPartner == null) return
      try {
        setLoading(true)
        const { data, meta } = await getMarketingSpend({
          filters: [
            { key: 'affiliate_id', value: String(selectedPartner) },
            { key: 'between_dates', value: `${startDate},${endDate}` },
          ],
          sort,
          page,
          per_page: 100,
        })
        setSpendData(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch {
        message.error('Failed to load spend data')
      } finally {
        setLoading(false)
      }
    },
    [startDate, endDate, selectedPartner, sortField],
  )

  useEffect(() => {
    if (selectedPartner == null) return
    fetchSpend()
  }, [selectedPartner, startDate, endDate, fetchSpend])

  const totalAdSpend = useMemo(
    () => spendData.reduce((sum, entry) => sum + entry.ad_spend, 0),
    [spendData],
  )

  const totalCreativeSpend = useMemo(
    () => spendData.reduce((sum, entry) => sum + entry.creative_spend, 0),
    [spendData],
  )

  const resetModal = () => {
    setModalOpen(false)
    setEditingRecord(null)
    setNewDate('')
    setNewAdSpend(null)
    setNewCreativeSpend(null)
  }

  const handleSave = async () => {
    if (!newDate) {
      message.error('Please enter a date')
      return
    }
    try {
      if (editingRecord?.id) {
        await updateMarketingSpend(String(editingRecord.id), {
          spend_date: newDate,
          affiliate_id: selectedPartner!,
          ad_spend: newAdSpend ?? 0,
          creative_spend: newCreativeSpend ?? 0,
        })
        message.success('Spend entry updated')
      } else {
        await createMarketingSpend([
          {
            spend_date: newDate,
            affiliate_id: selectedPartner!,
            ad_spend: newAdSpend ?? 0,
            creative_spend: newCreativeSpend ?? 0,
          },
        ])
        message.success('Spend entry added')
      }
      resetModal()
      fetchSpend()
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to save spend entry'))
    }
  }

  const handleEdit = (record: ManualSpendEntry) => {
    setEditingRecord(record)
    setNewDate(record.spend_date)
    setNewAdSpend(record.ad_spend)
    setNewCreativeSpend(record.creative_spend)
    setModalOpen(true)
  }

  const handleDelete = async (record: ManualSpendEntry) => {
    if (!record.id) return
    try {
      await deleteMarketingSpend(String(record.id))
      message.success('Spend entry deleted')
      fetchSpend()
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete spend entry'))
    }
  }

  const columns: TableProps<ManualSpendEntry>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'spend_date',
      key: 'spend_date',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Ad Spend',
      dataIndex: 'ad_spend',
      key: 'ad_spend',
      sorter: true,
      render: (value: number) => currencyFormatter.format(value),
      align: 'right',
    },
    ...(canViewCreativeSpend
      ? [
          {
            title: 'Creative Spend',
            dataIndex: 'creative_spend' as const,
            key: 'creative_spend',
            sorter: true,
            render: (value: number) => currencyFormatter.format(value),
            align: 'right' as const,
          },
        ]
      : []),
    ...(canUpdate || canDelete
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: ManualSpendEntry) => (
              <Flex gap="middle">
                {canUpdate && (
                  <Tooltip title="Edit">
                    <EditOutlined
                      style={{ fontSize: 16, cursor: 'pointer' }}
                      onClick={() => handleEdit(record)}
                    />
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete">
                    <DeleteOutlined
                      style={{
                        fontSize: 16,
                        color: '#ff4d4f',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleDelete(record)}
                    />
                  </Tooltip>
                )}
              </Flex>
            ),
          },
        ]
      : []),
  ]

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={16}>
          <Title level={3} style={{ margin: 0 }}>
            Manual Spend
          </Title>
          <Select
            value={selectedPartner ?? undefined}
            onChange={setSelectedPartner}
            options={partnerOptions}
            placeholder="Select partner"
            style={{ width: 300 }}
          />
        </Flex>
        <Flex gap={8} align="center">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ width: 160 }}
          />
          <span>to</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ width: 160 }}
          />
          {canCreate && (
            <Button type="primary" onClick={() => setModalOpen(true)}>
              New
            </Button>
          )}
        </Flex>
      </Flex>

      <Spin spinning={loading}>
        <Table<ManualSpendEntry>
          dataSource={spendData}
          columns={columns}
          rowKey={(record) =>
            record.id
              ? String(record.id)
              : `${record.spend_date}-${record.ad_spend}`
          }
          pagination={pagination}
          onChange={(p, _filters, sorter) => {
            const sort = resolveBackendSort(
              sorter,
              columnSortKeys,
              '-spend_date',
            )
            setSortField(sort)
            fetchSpend(p.current, sort)
          }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{currencyFormatter.format(totalAdSpend)}</strong>
              </Table.Summary.Cell>
              {canViewCreativeSpend && (
                <Table.Summary.Cell index={2} align="right">
                  <strong>
                    {currencyFormatter.format(totalCreativeSpend)}
                  </strong>
                </Table.Summary.Cell>
              )}
              {(canUpdate || canDelete) && (
                <Table.Summary.Cell index={canViewCreativeSpend ? 3 : 2} />
              )}
            </Table.Summary.Row>
          )}
        />
      </Spin>

      <Modal
        title={editingRecord ? 'Edit Spend Entry' : 'New Spend Entry'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={resetModal}
        okText={editingRecord ? 'Save' : 'Submit'}
      >
        <Flex vertical gap={16} style={{ marginTop: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Date</label>
            <Input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>
              Ad Spend ($)
            </label>
            <InputNumber
              value={newAdSpend}
              onChange={(value) => setNewAdSpend(value)}
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
              formatter={(value) =>
                value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
              }
              parser={(value) => Number(value?.replace(/,/g, '') || '0')}
            />
          </div>
          {canViewCreativeSpend && (
            <div>
              <label style={{ display: 'block', marginBottom: 4 }}>
                Creative Spend ($)
              </label>
              <InputNumber
                value={newCreativeSpend}
                onChange={(value) => setNewCreativeSpend(value)}
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
                formatter={(value) =>
                  value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
                }
                parser={(value) => Number(value?.replace(/,/g, '') || '0')}
              />
            </div>
          )}
        </Flex>
      </Modal>
    </div>
  )
}
